import moment from 'moment-timezone';

// Múi giờ mặc định cho ứng dụng
export const APP_TIMEZONE = 'Asia/Ho_Chi_Minh';

/**
 * Chuyển đổi thời gian từ UTC sang múi giờ địa phương để hiển thị
 * @param utcDate - Thời gian UTC từ server (string hoặc Date)
 * @param format - Định dạng hiển thị (mặc định: 'DD/MM/YYYY HH:mm')
 * @returns Chuỗi thời gian đã được chuyển đổi
 */
export const formatUTCToLocal = (utcDate: string | Date, format: string = 'DD/MM/YYYY HH:mm'): string => {
  if (!utcDate) return '';
  
  try {
    // Parse UTC từ server và chuyển về múi giờ địa phương
    const localDate = moment.utc(utcDate).tz(APP_TIMEZONE);
    return localDate.format(format);
  } catch (error) {
    console.error('Lỗi chuyển đổi timezone:', error);
    return '';
  }
};

/**
 * Chuyển đổi thời gian từ múi giờ địa phương sang UTC để gửi lên server
 * @param localDate - Thời gian địa phương (string hoặc Date)
 * @param format - Định dạng input (mặc định: 'YYYY-MM-DD HH:mm:ss')
 * @returns Chuỗi thời gian UTC
 */
export const formatLocalToUTC = (localDate: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
  if (!localDate) return '';
  
  try {
    // Parse thời gian địa phương và chuyển sang UTC
    let utcDate: moment.Moment;
    
    if (typeof localDate === 'string') {
      utcDate = moment.tz(localDate, format, APP_TIMEZONE).utc();
    } else {
      // Nếu là Date object, chuyển thành string trước
      const dateStr = moment(localDate).format(format);
      utcDate = moment.tz(dateStr, format, APP_TIMEZONE).utc();
    }
    
    return utcDate.toISOString();
  } catch (error) {
    console.error('Lỗi chuyển đổi timezone:', error);
    return '';
  }
};

/**
 * Lấy thời gian hiện tại theo múi giờ địa phương
 * @param format - Định dạng hiển thị (mặc định: 'YYYY-MM-DD HH:mm:ss')
 * @returns Chuỗi thời gian hiện tại
 */
export const getCurrentLocalTime = (format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
  return moment().tz(APP_TIMEZONE).format(format);
};

/**
 * Lấy thời gian hiện tại theo UTC
 * @param format - Định dạng hiển thị (mặc định: 'YYYY-MM-DD HH:mm:ss')
 * @returns Chuỗi thời gian UTC
 */
export const getCurrentUTCTime = (format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
  return moment().utc().format(format);
};

/**
 * Chuyển đổi ngày tháng cho input type="date" (chỉ lấy phần ngày)
 * @param utcDate - Thời gian UTC từ server
 * @returns Chuỗi ngày theo định dạng YYYY-MM-DD
 */
export const formatDateForInput = (utcDate: string | Date): string => {
  if (!utcDate) return '';
  
  try {
    const localDate = moment.utc(utcDate).tz(APP_TIMEZONE);
    return localDate.format('YYYY-MM-DD');
  } catch (error) {
    console.error('Lỗi chuyển đổi ngày cho input:', error);
    return '';
  }
};

/**
 * Chuyển đổi giờ cho input type="time" (chỉ lấy phần giờ)
 * @param utcDate - Thời gian UTC từ server
 * @returns Chuỗi giờ theo định dạng HH:mm
 */
export const formatTimeForInput = (utcDate: string | Date): string => {
  if (!utcDate) return '';
  
  try {
    const localDate = moment.utc(utcDate).tz(APP_TIMEZONE);
    return localDate.format('HH:mm');
  } catch (error) {
    console.error('Lỗi chuyển đổi giờ cho input:', error);
    return '';
  }
};

/**
 * Tạo datetime UTC từ ngày và giờ địa phương
 * @param date - Ngày theo định dạng YYYY-MM-DD
 * @param time - Giờ theo định dạng HH:mm
 * @returns Chuỗi datetime UTC
 */
export const createUTCDatetime = (date: string, time: string): string => {
  if (!date || !time) return '';
  
  try {
    const localDateTime = moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', APP_TIMEZONE);
    return localDateTime.utc().toISOString();
  } catch (error) {
    console.error('Lỗi tạo datetime UTC:', error);
    return '';
  }
};

/**
 * So sánh ngày tháng (chỉ so sánh phần ngày, bỏ qua giờ)
 * @param utcDate1 - Thời gian UTC thứ nhất
 * @param utcDate2 - Thời gian UTC thứ hai
 * @returns true nếu cùng ngày, false nếu khác ngày
 */
export const isSameDate = (utcDate1: string | Date, utcDate2: string | Date): boolean => {
  if (!utcDate1 || !utcDate2) return false;
  
  try {
    const localDate1 = moment.utc(utcDate1).tz(APP_TIMEZONE).startOf('day');
    const localDate2 = moment.utc(utcDate2).tz(APP_TIMEZONE).startOf('day');
    return localDate1.isSame(localDate2);
  } catch (error) {
    console.error('Lỗi so sánh ngày:', error);
    return false;
  }
};

/**
 * Kiểm tra xem thời gian có phải là hôm nay không
 * @param utcDate - Thời gian UTC cần kiểm tra
 * @returns true nếu là hôm nay, false nếu không
 */
export const isToday = (utcDate: string | Date): boolean => {
  if (!utcDate) return false;
  
  try {
    const today = moment().tz(APP_TIMEZONE).startOf('day');
    const checkDate = moment.utc(utcDate).tz(APP_TIMEZONE).startOf('day');
    return today.isSame(checkDate);
  } catch (error) {
    console.error('Lỗi kiểm tra hôm nay:', error);
    return false;
  }
};

/**
 * Lấy thời gian hiện tại theo múi giờ địa phương dưới dạng Date object
 * @returns Date object
 */
export const getCurrentLocalDate = (): Date => {
  return moment().tz(APP_TIMEZONE).toDate();
};

/**
 * Lấy thời gian hiện tại theo UTC dưới dạng Date object
 * @returns Date object
 */
export const getCurrentUTCDate = (): Date => {
  return moment().utc().toDate();
};

/**
 * Lấy thời gian địa phương từ UTC để xử lý logic
 * @param utcDate - Thời gian UTC từ server
 * @returns Moment object theo múi giờ địa phương
 */
export const getLocalMoment = (utcDate: string | Date): moment.Moment => {
  return moment.utc(utcDate).tz(APP_TIMEZONE);
};

/**
 * Kiểm tra xem thời gian có nằm trong khoảng thời gian không
 * @param utcDate - Thời gian UTC cần kiểm tra
 * @param startDate - Thời gian bắt đầu (local)
 * @param endDate - Thời gian kết thúc (local)
 * @returns true nếu nằm trong khoảng, false nếu không
 */
export const isBetween = (utcDate: string | Date, startDate: moment.Moment, endDate: moment.Moment): boolean => {
  if (!utcDate) return false;
  
  try {
    const localDate = getLocalMoment(utcDate);
    return localDate.isBetween(startDate, endDate, null, '[]');
  } catch (error) {
    console.error('Lỗi kiểm tra khoảng thời gian:', error);
    return false;
  }
};
